import { useEffect, useState } from 'react'

import type { SubjectData } from '@/components/Requests/types'
import { graphQLQuery } from '@/hooks/hooks'
import type { Activity } from '@/types/Activity'
import type { Recommendation, TrustedSourceRecommendation } from '@/types/Recommendation'
import {
  isOpenBankingRecommendation,
  isPayrollRecommendation,
  RequestType,
} from '@/types/Recommendation'

export interface UseRequestsDataResult {
  activeRecommendations: TrustedSourceRecommendation[]
  completeRecommendations: TrustedSourceRecommendation[]
  activeRequestActivity: Map<string, Activity[]>
  recommendationsCountByType: Record<RequestType, number>
  recommendationsLoading: boolean
  refreshRecommendations: () => Promise<void>
  refreshActivities: () => Promise<void>
}

type RecommendationsByType = Record<RequestType, TrustedSourceRecommendation[]>

export const useRequestsData = (
  subjectData: SubjectData,
  activeRequestType: RequestType,
): UseRequestsDataResult => {
  const [activeByType, setActiveByType] = useState<RecommendationsByType>({
    [RequestType.OPEN_BANKING]: [],
    [RequestType.PAYROLL]: [],
  })
  const [completeByType, setCompleteByType] = useState<RecommendationsByType>({
    [RequestType.OPEN_BANKING]: [],
    [RequestType.PAYROLL]: [],
  })

  const [activeRequestActivity, setActiveRequestActivity] = useState<
    Map<string, Activity[]>
  >(new Map())
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)

  useEffect(() => {
    refreshActivities()
    refreshRecommendations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshActivities = async () => {
    const response = await graphQLQuery({
      query: `query{
        getActivityFeedForSubject(cusId: "${subjectData.customerId}" appId: "${subjectData.applicationId}" subId: "${subjectData.subjectId}"){
          title
          desc
          timestamp
          message
          type
          initiatedBy
          createdBy,
          recommendationId
        }
      }`,
    })

    const { data } = await response.json()

    const nextActiveRequestActivity = new Map<string, Activity[]>()

    if (data && data.getActivityFeedForSubject) {
      const activities: Activity[] = data.getActivityFeedForSubject
      activities.forEach(activity => {
        const { recommendationId } = activity

        if (recommendationId === null || recommendationId === undefined) {
          return
        }

        const requestActivities = nextActiveRequestActivity.get(recommendationId)

        if (requestActivities) {
          requestActivities.push(activity)
        } else {
          nextActiveRequestActivity.set(recommendationId, [activity])
        }
      })
    }

    setActiveRequestActivity(nextActiveRequestActivity)
  }

  const refreshRecommendations = async () => {
    setRecommendationsLoading(true)

    try {
      const response = await graphQLQuery({
        query: `query{
        recommendations(customerId: "${subjectData.customerId}" applicationId: "${subjectData.applicationId}" subjectId: "${subjectData.subjectId}"){
          id
          category
          displayName
          description
          completed
          createdAt
          updatedAt
          requestMessage
          author
          status
          optOutReason {
            category
            additionalInformation
          }
          connectionFailures {
            timestamp
            reason
          }
        }
      }`,
      })

      const { data } = await response.json()

      if (data && data.recommendations) {
        const recommendations: Recommendation[] = data.recommendations

        const nextActiveByType: RecommendationsByType = {
          [RequestType.OPEN_BANKING]: [],
          [RequestType.PAYROLL]: [],
        }
        const nextCompleteByType: RecommendationsByType = {
          [RequestType.OPEN_BANKING]: [],
          [RequestType.PAYROLL]: [],
        }

        recommendations.forEach(recommendation => {
          if (isOpenBankingRecommendation(recommendation)) {
            const target = recommendation.completed
              ? nextCompleteByType
              : nextActiveByType
            target[RequestType.OPEN_BANKING].push(recommendation)
            return
          }

          if (isPayrollRecommendation(recommendation)) {
            const target = recommendation.completed
              ? nextCompleteByType
              : nextActiveByType
            target[RequestType.PAYROLL].push(recommendation)
          }
        })

        setActiveByType(nextActiveByType)
        setCompleteByType(nextCompleteByType)
      }
    } finally {
      setRecommendationsLoading(false)
    }
  }

  const activeRecommendations = activeByType[activeRequestType]
  const completeRecommendations = completeByType[activeRequestType]

  const recommendationsCountByType: Record<RequestType, number> = {
    [RequestType.OPEN_BANKING]:
      activeByType[RequestType.OPEN_BANKING].length +
      completeByType[RequestType.OPEN_BANKING].length,
    [RequestType.PAYROLL]:
      activeByType[RequestType.PAYROLL].length +
      completeByType[RequestType.PAYROLL].length,
  }

  return {
    activeRecommendations,
    completeRecommendations,
    activeRequestActivity,
    recommendationsCountByType,
    recommendationsLoading,
    refreshRecommendations,
    refreshActivities,
  }
}
